
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { Loader2, CheckCircle, XCircle, Eye } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

export function RSSFeedTester() {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const testFeeds = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('test-rss-feeds', {});

      if (error) {
        console.error('Error testing RSS feeds:', error);
        setError(`Failed to test RSS feeds: ${error.message}`);
        toast({
          title: "Error",
          description: `Failed to test RSS feeds: ${error.message}`,
          variant: "destructive"
        });
        return;
      }

      if (data && data.results) {
        setResults(data.results);
        
        // Count successful feeds
        const successCount = data.results.filter((result: any) => result.success).length;
        
        toast({
          title: "Test Complete",
          description: `Successfully parsed ${successCount} of ${data.results.length} RSS feeds`,
        });
      }
      
      console.log('RSS test response:', data);
    } catch (error) {
      console.error('Error invoking function:', error);
      setError(`An unexpected error occurred: ${error.message}`);
      toast({
        title: "Error",
        description: `An unexpected error occurred: ${error.message}`,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>RSS Feed Tester</CardTitle>
        <CardDescription>
          Test connectivity and parsing of RSS feeds
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {results.length > 0 && (
            <Accordion type="single" collapsible className="w-full">
              {results.map((result, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="flex gap-2">
                    {result.success ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                    {result.source}
                  </AccordionTrigger>
                  <AccordionContent>
                    {result.success ? (
                      <div className="space-y-4">
                        <div>
                          <p className="font-semibold">Feed Title:</p>
                          <p>{result.feedTitle}</p>
                        </div>
                        <div>
                          <p className="font-semibold">Items Found: {result.itemCount}</p>
                        </div>
                        
                        {result.sampleItems && result.sampleItems.length > 0 && (
                          <div className="space-y-4">
                            <p className="font-semibold">Sample Items:</p>
                            
                            {result.sampleItems.map((item: any, i: number) => (
                              <Card key={i} className="overflow-hidden">
                                <CardHeader className="p-4 pb-2">
                                  <CardTitle className="text-lg">{item.title}</CardTitle>
                                  <CardDescription>
                                    Published: {new Date(item.pubDate).toLocaleString()}
                                  </CardDescription>
                                </CardHeader>
                                <CardContent className="p-4 pt-0">
                                  {item.image && (
                                    <div className="mb-2 aspect-video overflow-hidden rounded-md">
                                      <img 
                                        src={item.image} 
                                        alt={item.title}
                                        className="h-full w-full object-cover"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                      />
                                    </div>
                                  )}
                                  <div className="line-clamp-2 text-sm text-muted-foreground">
                                    {/* Remove HTML tags for display */}
                                    {item.description.replace(/<[^>]*>?/gm, '')}
                                  </div>
                                </CardContent>
                                <CardFooter className="p-4 pt-0">
                                  <Button 
                                    asChild 
                                    variant="outline" 
                                    size="sm"
                                    className="gap-1"
                                  >
                                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                                      <Eye className="h-4 w-4" />
                                      View Article
                                    </a>
                                  </Button>
                                </CardFooter>
                              </Card>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-red-500">
                        Error: {result.error}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={testFeeds} 
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Testing Feeds...
            </>
          ) : (
            "Test RSS Feeds"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

export default RSSFeedTester;
